/**
 * The asset store is the disk. Assets are keyed by sha256 (technical-design §11.2)
 * so a rotated signed URL never forces a re-download, and partial downloads are
 * kept separately so a range request can resume them (§11.5).
 *
 * Two implementations exist behind this interface: an in-memory one, which the
 * headless harness drives, and an IndexedDB one for the WebView.
 */

export interface CachedAssetMeta {
  readonly sha256: string;
  readonly byte_length: number;
  readonly media_type: string;
  readonly stored_at_ms: number;
  readonly last_access_ms: number;
}

export interface CachedAsset extends CachedAssetMeta {
  readonly bytes: Uint8Array;
}

export interface AssetStore {
  has(sha256: string): Promise<boolean>;
  get(sha256: string): Promise<CachedAsset | null>;
  put(asset: {
    sha256: string;
    bytes: Uint8Array;
    media_type: string;
    at_ms: number;
  }): Promise<void>;
  delete(sha256: string): Promise<void>;
  list(): Promise<readonly CachedAssetMeta[]>;
  touch(sha256: string, atMs: number): Promise<void>;
  totalBytes(): Promise<number>;
  /** Bytes of an interrupted download, for a range resume. */
  readPartial(sha256: string): Promise<Uint8Array | null>;
  writePartial(sha256: string, bytes: Uint8Array): Promise<void>;
  clearPartial(sha256: string): Promise<void>;
}

export function createMemoryAssetStore(): AssetStore {
  const assets = new Map<string, CachedAsset>();
  const partials = new Map<string, Uint8Array>();

  return {
    has: async (sha256) => assets.has(sha256),
    get: async (sha256) => assets.get(sha256) ?? null,
    put: async ({ sha256, bytes, media_type, at_ms }) => {
      assets.set(sha256, {
        sha256,
        bytes: copy(bytes),
        byte_length: bytes.byteLength,
        media_type,
        stored_at_ms: at_ms,
        last_access_ms: at_ms,
      });
    },
    delete: async (sha256) => {
      assets.delete(sha256);
    },
    list: async () =>
      [...assets.values()].map((asset) => ({
        sha256: asset.sha256,
        byte_length: asset.byte_length,
        media_type: asset.media_type,
        stored_at_ms: asset.stored_at_ms,
        last_access_ms: asset.last_access_ms,
      })),
    touch: async (sha256, atMs) => {
      const existing = assets.get(sha256);
      if (existing !== undefined) assets.set(sha256, { ...existing, last_access_ms: atMs });
    },
    totalBytes: async () =>
      [...assets.values()].reduce((total, asset) => total + asset.byte_length, 0),
    readPartial: async (sha256) => partials.get(sha256) ?? null,
    writePartial: async (sha256, bytes) => {
      partials.set(sha256, copy(bytes));
    },
    clearPartial: async (sha256) => {
      partials.delete(sha256);
    },
  };
}

/**
 * The store owns what it was handed. IndexedDB gets this for free — it
 * structure-clones on the way in — and the in-memory store has to do it by hand, or a
 * caller that reuses a buffer silently rewrites bytes that have already been verified
 * against their digest. Copying here also keeps the two implementations honest: the
 * harness substitutes this one for the device's, and a substitute that behaves
 * differently from the real thing is worse than no substitute at all.
 */
function copy(bytes: Uint8Array): Uint8Array {
  const out = new Uint8Array(bytes.byteLength);
  out.set(bytes);
  return out;
}
